import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import { useSeo } from "@/hooks/useSeo";
import { milestones, principles, testimonials } from "@/mocks/about";
import { siteStats } from "@/mocks/home";
import ContactForm from "./components/ContactForm";

const positioning = [
  {
    icon: "ri-focus-3-line",
    title: "我们是什么",
    desc: "一个把评分权交回给网友的爆料内容评选平台，用群众打分代替少数人拍板。",
  },
  {
    icon: "ri-user-heart-line",
    title: "为谁而做",
    desc: "为喜欢围观热门话题、乐于表达观点的普通网友而做，让每个人都能参与评选。",
  },
  {
    icon: "ri-lightbulb-line",
    title: "想解决什么",
    desc: "让投票结果看得见、说得清、查得到来源，减少暗箱刷榜带来的不信任。",
  },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 爆料大赛的定位与内容原则 | 爆料大赛",
    description:
      "了解爆料大赛的创办初衷、网站定位、内容原则与发展理念，查看历届评选历程、用户口碑，并通过反馈表单与我们取得联系。",
    keywords: "爆料大赛关于我们,爆料大赛内容原则,爆料大赛介绍,爆料大赛评选理念",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于爆料大赛",
        inLanguage: "zh-CN",
        description: "爆料大赛的创办背景、网站定位、内容原则与发展理念介绍。",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "爆料大赛",
        url: "/",
        email: "hello@baoliaodasai.net",
        description: "面向网友的趣味爆料内容评选活动，由群众打分决定排名结果。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "/about" },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />
      <main className="flex-1 pt-[68px]">
        <div className="w-full bg-background-100 border-b border-background-200">
          <div className="mx-auto max-w-6xl px-4 md:px-8 py-10 md:py-14">
            <Breadcrumb items={[{ label: "关于我们" }]} />
            <h1 className="mt-4 font-heading text-3xl md:text-5xl tracking-wide text-foreground-950">
              关于爆料大赛
            </h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base leading-relaxed text-foreground-600">
              我们把「谁最出圈」这件事，交给参与的人一起决定。这里记录着我们的定位、原则，以及一路走来的选择。
            </p>
          </div>
        </div>

        <section className="py-16 md:py-24">
          <div className="w-full px-4 md:px-8">
            <div className="mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div className="rounded-lg overflow-hidden border border-background-200 w-full h-[280px] md:h-[420px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Editorial%20flat%20vector%20illustration%20of%20diverse%20anonymous%20people%20gathering%20around%20a%20large%20scoreboard%20panel%2C%20warm%20cream%20background%20with%20red%20gold%20and%20green%20accents%2C%20community%20collaboration%20poster%20composition%2C%20bold%20geometric%20shapes&width=1000&height=1100&seq=baoliao-about-01&orientation=portrait"
                  alt="网友聚集在评分看板前参与爆料大赛评选的插画"
                  title="爆料大赛社区评选理念"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <SectionHeading eyebrow="品牌介绍" title="一个由网友共同打分的评选舞台" />
                <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-700">
                  爆料大赛最初只是社区里的一次小投票，几十件投稿、几个人打分。随着参与的人越来越多，我们逐渐把评分机制做得更透明：去掉极值、公开走势、先审后展。
                </p>
                <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
                  现在的爆料大赛，既有轻松有趣的沙雕名镜头，也有让人心里一动的温柔时刻。我们不追求热闹的噱头，只想让每一件被认真投稿的作品，都能被公平地看见。
                </p>
                <dl className="mt-8 grid grid-cols-2 gap-5">
                  {siteStats.map((stat) => (
                    <div key={stat.label} className="border-l-2 border-accent-500 pl-4">
                      <dt className="text-xs text-foreground-500">{stat.label}</dt>
                      <dd className="mt-1 font-heading text-2xl text-foreground-950 tracking-wide">{stat.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-16 md:py-24">
          <div className="w-full px-4 md:px-8">
            <div className="mx-auto max-w-6xl">
              <SectionHeading eyebrow="网站定位" title="我们存在的意义，说清楚一点" align="center" />
              <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                {positioning.map((item) => (
                  <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                    <span className="w-12 h-12 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center text-2xl mb-4">
                      <i className={item.icon} />
                    </span>
                    <h3 className="font-heading text-lg tracking-wide text-foreground-950 mb-2">{item.title}</h3>
                    <p className="text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="w-full px-4 md:px-8">
            <div className="mx-auto max-w-6xl">
              <SectionHeading
                eyebrow="内容原则"
                title="四条我们一直坚持的底线"
                desc="评选可以有热度，但不能没有底线。以下原则适用于所有入围作品。"
              />
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
                {principles.map((item) => (
                  <div key={item.title} className="flex items-start gap-4 rounded-lg border border-background-200 p-6">
                    <span className="shrink-0 w-11 h-11 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center text-xl">
                      <i className={item.icon} />
                    </span>
                    <div>
                      <h3 className="font-medium text-foreground-950 mb-1.5">{item.title}</h3>
                      <p className="text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-primary-950 py-16 md:py-24">
          <div className="w-full px-4 md:px-8">
            <div className="mx-auto max-w-6xl">
              <div className="max-w-2xl">
                <p className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-accent-400 uppercase mb-3">
                  <span className="w-6 h-px bg-accent-400 inline-block" />
                  发展理念
                </p>
                <h2 className="font-heading text-2xl md:text-4xl tracking-wide text-background-50">
                  一届一届，把规则做得更扎实
                </h2>
              </div>
              <ol className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-8">
                {milestones.map((item) => (
                  <li key={item.year} className="relative pt-6 border-t-2 border-background-50/20">
                    <span className="absolute -top-px left-0 w-14 h-0.5 bg-accent-500" />
                    <span className="font-heading text-lg text-accent-400">{item.year}</span>
                    <h3 className="mt-2 font-medium text-background-50">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-background-200">{item.desc}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="w-full px-4 md:px-8">
            <div className="mx-auto max-w-6xl">
              <SectionHeading eyebrow="用户口碑" title="参与者怎么说" align="center" />
              <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                {testimonials.map((item) => (
                  <blockquote key={item.name} className="rounded-lg border border-background-200 bg-background-50 p-6 flex flex-col">
                    <i className="ri-double-quotes-l text-3xl text-primary-300 mb-3" />
                    <p className="text-sm leading-relaxed text-foreground-700 flex-1">{item.text}</p>
                    <footer className="mt-5 pt-4 border-t border-background-200">
                      <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </footer>
                  </blockquote>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-16 md:py-24">
          <div className="w-full px-4 md:px-8">
            <div className="mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <SectionHeading eyebrow="联系我们" title="有建议，或者发现异常刷分" />
                <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-700">
                  如果你对评选规则有建议，或发现疑似刷分、抄袭、涉及隐私的内容，欢迎通过右侧表单告诉我们。我们会在核实后处理，并同步调整受影响的排名。
                </p>
                <ul className="mt-8 space-y-4">
                  <li className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-md bg-background-50 border border-background-200 flex items-center justify-center text-lg text-primary-600">
                      <i className="ri-mail-line" />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">反馈邮箱</p>
                      <a href="mailto:hello@baoliaodasai.net" className="text-sm text-foreground-900 hover:text-primary-600 transition-colors">
                        hello@baoliaodasai.net
                      </a>
                    </div>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-md bg-background-50 border border-background-200 flex items-center justify-center text-lg text-primary-600">
                      <i className="ri-time-line" />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">处理周期</p>
                      <p className="text-sm text-foreground-900">一般在 3 个工作日内答复</p>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="lg:col-span-7">
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}